// V2 reconnect banner. Surfaces "Opponent disconnected — Xs to reconnect"
// with a wall-clock countdown driven by `closesAtMs`. ClientApp routes the
// `opponent_disconnected` / `opponent_reconnected` lifecycle events here;
// `match_ended` also hides the banner (the EOM modal takes over).
//
// Banner is a presentation-only DOM element (Rule 2). It owns no
// gameplay state — `closesAtMs` is the absolute wall-clock time the
// server's reconnect window closes, and we just subtract `Date.now()`
// every animation frame. If the page is throttled (background tab) the
// countdown stays accurate because it's wall-clock-based.

const ROOT_ID = 'reconnect-banner';
const STYLE_ID = 'reconnect-banner-styles';

// Default banner label — used for the V2 forfeit path (one or both
// players didn't opt into Reconnect-Bot, so the remaining player is
// staring at an empty side until either reconnect or timeout).
const DEFAULT_LABEL = (remainingSec: number): string =>
  `Opponent disconnected — ${remainingSec}s to reconnect`;

export type ReconnectBannerLabel = (remainingSec: number) => string;

export class ReconnectBanner {
  private root: HTMLDivElement | null = null;
  private label: HTMLDivElement | null = null;
  private retryBtn: HTMLButtonElement | null = null;
  private onRetry: (() => void) | null = null;
  private retryClickHandler: (() => void) | null = null;
  private rafHandle: number | null = null;
  private closesAtMs = 0;
  // Label template lets ClientApp swap between the V2 default
  // ("Opponent disconnected — Xs to reconnect") and the V2.5+
  // Reconnect-Bot label ("Bot playing for opponent — Xs to take back
  // over") without re-mounting the DOM element. The countdown
  // machinery (`closesAtMs` + rAF tick) is identical for both flows.
  private currentLabel: ReconnectBannerLabel = DEFAULT_LABEL;

  constructor() {
    injectStyles();
    this.mount();
    this.hide();
  }

  show(closesAtMs: number): void {
    this.closesAtMs = closesAtMs;
    if (this.root === null) return;
    if (this.retryBtn !== null) this.retryBtn.style.display = 'none';
    this.root.style.display = 'flex';
    this.tick();
  }

  // V1.8 — static "transport error" mode. Used for WebSocket transport
  // failures (captive portal, mobile proxy drop). No countdown — the
  // banner stays up until the player retries or leaves the match. The
  // retry callback is responsible for whatever rebooting the connection
  // needs (typically a page reload or `app.startMatch` from current
  // boot params).
  showError(message: string, onRetry?: () => void): void {
    if (this.root === null || this.label === null) return;
    // Cancel any running countdown — the static label owns the banner now.
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.label.textContent = message;
    this.onRetry = onRetry ?? null;
    if (this.retryBtn !== null) {
      this.retryBtn.style.display = onRetry !== undefined ? 'inline-block' : 'none';
    }
    this.root.style.display = 'flex';
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    if (this.retryBtn !== null) this.retryBtn.style.display = 'none';
    this.onRetry = null;
  }

  // Swap the label template. If the banner is currently visible, paint
  // the new label immediately rather than waiting for the next rAF —
  // a fast `bot_reconnect_filled` arriving right after
  // `opponent_disconnected` would otherwise show a stale label for one
  // animation frame.
  setLabelTemplate(template: ReconnectBannerLabel): void {
    this.currentLabel = template;
    if (this.root !== null && this.root.style.display !== 'none') {
      this.tick();
    }
  }

  // Reset to the V2 default — useful when transitioning out of a
  // bot-fill state back to the regular forfeit countdown.
  resetLabelTemplate(): void {
    this.setLabelTemplate(DEFAULT_LABEL);
  }

  private tick = (): void => {
    if (this.label === null) return;
    const remainingMs = Math.max(0, this.closesAtMs - Date.now());
    const remainingSec = Math.ceil(remainingMs / 1000);
    this.label.textContent = this.currentLabel(remainingSec);
    if (remainingMs > 0) {
      this.rafHandle = requestAnimationFrame(this.tick);
    } else {
      this.rafHandle = null;
    }
  };

  private mount(): void {
    this.root = ensureRoot();
    this.root.innerHTML = '';
    this.label = document.createElement('div');
    this.label.className = 'reconnect-banner__label';
    this.root.appendChild(this.label);
    this.retryBtn = document.createElement('button');
    this.retryBtn.type = 'button';
    this.retryBtn.className = 'reconnect-banner__retry';
    this.retryBtn.textContent = 'RETRY';
    this.retryBtn.style.display = 'none';
    this.retryClickHandler = (): void => {
      const cb = this.onRetry;
      if (cb !== null) cb();
    };
    this.retryBtn.addEventListener('click', this.retryClickHandler);
    this.root.appendChild(this.retryBtn);
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 25;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
/* V2 Tempo polish: matches HUD chrome — same dark gradient + hex clip
 * + cyan rim glow as .hud__resources / .mission-banner — but tinted red
 * because this banner only appears on transport errors. */
.reconnect-banner__label {
  padding: 10px 18px;
  background: linear-gradient(180deg, rgba(60,18,18,0.92), rgba(30,8,8,0.94));
  border: 1px solid rgba(255, 122, 90, 0.55);
  border-radius: 0;
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  color: #ffd9b0;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  box-shadow: 0 0 0 1px rgba(255,122,90,0.10) inset, 0 0 20px rgba(255,122,90,0.25), 0 6px 18px rgba(0,0,0,0.5);
  touch-action: manipulation;
}
.reconnect-banner__retry {
  margin-left: 12px;
  padding: 8px 18px;
  background: linear-gradient(180deg, rgba(255, 42, 42, 0.18), rgba(60,18,18,0.85));
  border: 1px solid rgba(255, 122, 90, 0.55);
  border-radius: 0;
  clip-path: polygon(8px 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0 50%);
  color: #fce6c8;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 150ms, box-shadow 150ms, transform 80ms;
  min-height: 36px;
}
.reconnect-banner__retry:hover {
  color: #ffd9b0;
  box-shadow: 0 0 14px rgba(255, 122, 90, 0.45);
}
.reconnect-banner__retry:active { transform: scale(0.98); }
/* V3 mobile: slightly larger padding and font so the countdown is easy
 * to read under stress; ensure the banner clears the iOS safe-area at
 * the top of the screen. */
@media (max-width: 820px), (pointer: coarse) {
  #${ROOT_ID} {
    top: calc(12px + env(safe-area-inset-top, 0px));
  }
  .reconnect-banner__label {
    font-size: 12px;
    padding: 10px 20px;
    max-width: calc(100vw - 24px);
    text-align: center;
  }
}
`;
  document.head.appendChild(style);
}
